import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_CHANNEL_LABELS,
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_LABELS,
} from "@onadssuper/validators";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Megaphone, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  PageSpinner,
  Select,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "../../../../components/ui";
import { formatBudget } from "../../../../features/campaigns/money";
import { NextActionText } from "../../../../features/campaigns/next-action";
import { CampaignStatusBadge } from "../../../../features/campaigns/status-badge";
import { useAgencyMembers } from "../../../../features/campaigns/use-agency-members";
import { useCan, useCurrentWorkspace } from "../../../../features/workspace/workspace-context";
import { Avatar } from "../../../../lib/avatar";
import { useTRPC } from "../../../../lib/trpc";
import { formatCalendarDate } from "../../../../lib/utils";

/** Filters live in the URL, so a filtered portfolio can be shared or bookmarked. */
const searchSchema = z.object({
  /** Absent = "open" (everything except archived). */
  status: z
    .enum([...CAMPAIGN_STATUSES, "open", "all"])
    .optional()
    .catch(undefined),
  channel: z.enum(CAMPAIGN_CHANNELS).optional().catch(undefined),
  owner: z.uuid().optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
});
type PortfolioSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/w/$slug/campaigns/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: Portfolio,
});

const ALL = "__all";

function Portfolio() {
  const workspace = useCurrentWorkspace();
  const canCreate = useCan("campaign.create");
  return (
    <>
      <PageHeader
        title="Campaigns"
        description="Every campaign in this workspace, with its owner and status."
        actions={
          canCreate && (
            <Button asChild>
              <Link to="/w/$slug/campaigns/new" params={{ slug: workspace.slug }}>
                <Plus className="size-4" /> New campaign
              </Link>
            </Button>
          )
        }
      />
      <Filters />
      <CampaignTable />
    </>
  );
}

function Filters() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const owners = useAgencyMembers();
  const [q, setQ] = useState(search.q ?? "");
  const update = (patch: Partial<PortfolioSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? "") !== q) update({ q: q || undefined });
    }, 300);
    return () => clearTimeout(t);
  });

  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-3 left-3 size-4 text-subtle"
          aria-hidden
        />
        <Input
          className="pl-9"
          type="search"
          aria-label="Search campaigns"
          placeholder="Search by name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <Select
        aria-label="Status"
        value={search.status ?? "open"}
        onValueChange={(status) => update({ status })}
        options={[
          { value: "open", label: "All open" },
          ...CAMPAIGN_STATUSES.map((s) => ({ value: s, label: CAMPAIGN_STATUS_LABELS[s] })),
          { value: "all", label: "Everything (incl. archived)" },
        ]}
      />
      <Select
        aria-label="Channel"
        value={search.channel ?? ALL}
        onValueChange={(v) => update({ channel: v === ALL ? undefined : v })}
        options={[
          { value: ALL, label: "All channels" },
          ...CAMPAIGN_CHANNELS.map((c) => ({ value: c, label: CAMPAIGN_CHANNEL_LABELS[c] })),
        ]}
      />
      <Select
        aria-label="Owner"
        value={search.owner ?? ALL}
        onValueChange={(v) => update({ owner: v === ALL ? undefined : v })}
        options={[
          { value: ALL, label: "Any owner" },
          ...(owners.data ?? []).map((m) => ({ value: m.userId, label: m.name })),
        ]}
      />
    </div>
  );
}

function CampaignTable() {
  const trpc = useTRPC();
  const workspace = useCurrentWorkspace();
  const search = Route.useSearch();
  const status = search.status ?? "open";
  const statuses =
    status === "open" ? undefined : status === "all" ? [...CAMPAIGN_STATUSES] : [status];
  const campaigns = useInfiniteQuery(
    trpc.campaigns.list.infiniteQueryOptions(
      {
        limit: 25,
        statuses,
        channel: search.channel,
        ownerUserId: search.owner,
        search: search.q,
      },
      { getNextPageParam: (last) => last.nextCursor ?? undefined },
    ),
  );

  if (campaigns.isPending) return <PageSpinner />;
  if (campaigns.isError)
    return <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} />;
  const rows = campaigns.data.pages.flatMap((p) => p.items);
  const filtered = Boolean(search.q || search.channel || search.owner || status !== "open");

  if (rows.length === 0)
    return (
      <Card>
        <EmptyState
          icon={<Megaphone className="size-8" />}
          title={filtered ? "No campaigns match these filters" : "No campaigns yet"}
          description={
            filtered
              ? "Try another status, channel or owner."
              : "Campaigns you create appear here, with their owner and status."
          }
        />
      </Card>
    );

  return (
    <Card>
      <Table>
        <THead>
          <TR>
            <TH>Campaign</TH>
            <TH>Status</TH>
            <TH>Next</TH>
            <TH>Owner</TH>
            <TH>Dates</TH>
            <TH>Channels</TH>
            <TH className="text-right">Budget</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((c) => (
            <TR key={c.id}>
              <TD className="min-w-40 wrap-anywhere">
                <Link
                  to="/w/$slug/campaigns/$campaignId"
                  params={{ slug: workspace.slug, campaignId: c.id }}
                  className="font-medium text-text hover:text-primary hover:underline"
                >
                  {c.name}
                </Link>
              </TD>
              <TD>
                <CampaignStatusBadge status={c.status} />
              </TD>
              <TD className="min-w-36 text-sm">
                <NextActionText action={c.nextAction} />
              </TD>
              <TD>
                <div className="flex items-center gap-2">
                  <Avatar name={c.owner.name} image={c.owner.image} />
                  <span className="truncate">{c.owner.name}</span>
                </div>
              </TD>
              <TD className="min-w-28 text-muted">
                {c.startsOn ? formatCalendarDate(c.startsOn) : "—"} –{" "}
                {c.endsOn ? formatCalendarDate(c.endsOn) : "—"}
              </TD>
              <TD className="text-muted">
                {c.channels.map((ch) => CAMPAIGN_CHANNEL_LABELS[ch]).join(", ") || "—"}
              </TD>
              <TD className="text-right whitespace-nowrap">
                {formatBudget(c.budgetAmountMinor, c.budgetCurrency)}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {campaigns.hasNextPage && (
        <div className="flex justify-center border-t border-border p-3">
          <Button
            variant="ghost"
            loading={campaigns.isFetchingNextPage}
            onClick={() => void campaigns.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
    </Card>
  );
}
